import { NamedOperation } from 'operationlib';
import { buildOperationChatSpan } from 'src/app/features/mce-editor/extensions/operation/operation-chat-span';

/** Marker inline per tiri nelle risposte suggerite: `{{roll:0}}`, `{{ roll: 0 }}`, … */
export const SUGGESTED_MESSAGE_ROLL_MARKER_RE = /\{\{\s*roll\s*:\s*(\d+)\s*\}\}/gi;

export const MAX_REQUESTED_OPERATIONS_PER_CHOICE = 1;

export type SuggestedMessageRollLabel = {
  name: string;
  description?: string;
};

function escapeHtml(s: string): string {
  return s.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

export function stripRollMarkers(message: string): string {
  return message
    .replace(SUGGESTED_MESSAGE_ROLL_MARKER_RE, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function messageHasRollMarkers(message: string): boolean {
  return new RegExp(SUGGESTED_MESSAGE_ROLL_MARKER_RE.source, 'i').test(message);
}

export function countDistinctRollMarkerIndices(message: string): number {
  const indices = new Set<number>();
  for (const m of message.matchAll(SUGGESTED_MESSAGE_ROLL_MARKER_RE)) {
    const idx = Number.parseInt(m[1] ?? '', 10);
    if (Number.isInteger(idx) && idx >= 0) indices.add(idx);
  }
  return indices.size;
}

/** Descrizioni dei tiri da mostrare sotto la preview del messaggio. */
export function formatSuggestedMessageRollDescriptions(
  ops: SuggestedMessageRollLabel[],
): string | null {
  if (!ops.length) return null;
  const blocks = ops
    .map((op) => {
      const desc = op.description?.trim();
      if (!desc) return null;
      return ops.length > 1 ? `${op.name}: ${desc}` : desc;
    })
    .filter((block): block is string => block !== null);
  return blocks.length > 0 ? blocks.join('\n\n') : null;
}

/** Preview testuale: sostituisce i marker con chip leggibili (es. [🎲 Scassinare]). */
export function formatSuggestedMessagePreview(
  message: string,
  ops: SuggestedMessageRollLabel[],
): string {
  SUGGESTED_MESSAGE_ROLL_MARKER_RE.lastIndex = 0;
  return message.replace(SUGGESTED_MESSAGE_ROLL_MARKER_RE, (_match, idxStr: string) => {
    const idx = Number.parseInt(idxStr, 10);
    const op = ops[idx] ?? (ops.length === 1 ? ops[0] : undefined);
    return op ? `[🎲 ${op.name}]` : '[🎲]';
  });
}

/**
 * Compone l'HTML del messaggio chat sostituendo i marker con span operazione.
 * Se ci sono operazioni ma nessun marker, appende il tiro a fine messaggio.
 */
export function composeSuggestedMessageHtml(
  message: string,
  rolls: NamedOperation[],
  characterId: number,
  characterName: string,
): string {
  const hasMarkers = messageHasRollMarkers(message);

  if (!hasMarkers && rolls.length > 0) {
    const textHtml = escapeHtml(stripRollMarkers(message));
    const span = buildOperationChatSpan(rolls[0]!, characterId, characterName);
    return `${textHtml}${textHtml.length > 0 ? ' ' : ''}${span}`;
  }

  let result = '';
  let lastIndex = 0;
  for (const m of message.matchAll(SUGGESTED_MESSAGE_ROLL_MARKER_RE)) {
    const start = m.index ?? 0;
    result += escapeHtml(message.slice(lastIndex, start));
    const idx = Number.parseInt(m[1] ?? '', 10);
    const roll = rolls[idx];
    if (roll) {
      result += buildOperationChatSpan(roll, characterId, characterName);
    }
    lastIndex = start + m[0].length;
  }
  result += escapeHtml(message.slice(lastIndex));

  // Safety: never leave raw markers in the outgoing HTML.
  return stripRollMarkersFromHtml(result);
}

/** Removes any leftover roll markers from composed HTML (plain-text segments only). */
function stripRollMarkersFromHtml(html: string): string {
  return html.replace(SUGGESTED_MESSAGE_ROLL_MARKER_RE, '');
}
